import React from 'react';

export default function WabaTemplateMessage({ message }) {
  // Helper function to check if this is a WABA template message
  const isWabaTemplateMessage = (message) => {
    if (message.message_content) {
      try {
        const content = JSON.parse(message.message_content);
        return content.header !== undefined || content.body !== undefined || content.footer !== undefined;
      } catch (e) {
        // Not JSON, continue
      }
    }
    return false;
  };

  // Helper function to substitute parameters in text
  const substituteParameters = (text, contentVariables) => {
    if (!text || !contentVariables) return text;

    let substitutedText = text;
    Object.keys(contentVariables).forEach(key => {
      const placeholder = `{{${key}}}`;
      const value = contentVariables[key];
      substitutedText = substitutedText.replace(new RegExp(placeholder, 'g'), value);
    });

    return substitutedText;
  };

  // Helper function to format text with line breaks
  const formatText = (text) => {
    if (!text) return '';
    return text
      .replace(/\r\n/g, '<br/>')
      .replace(/\n/g, '<br/>')
      .replace(/\r/g, '<br/>');
  };

  // Check if this is a WABA template message
  if (!isWabaTemplateMessage(message)) {
    return null;
  }

  let templateData = null;
  try {
    templateData = JSON.parse(message.message_content);
  } catch (e) {
    console.error('Error parsing WABA template message content:', e);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-2">
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-red-700 text-sm">Error parsing template message</span>
        </div>
      </div>
    );
  }

  const { header, body, footer, content_variables } = templateData;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2">
      <div className="flex items-center space-x-2 mb-3">
        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
        <span className="text-blue-800 font-medium">WhatsApp Business Template</span>
      </div>

      <div className="space-y-3">
        {/* Header */}
        {header && header.text && (
          <div className="bg-white border border-gray-200 rounded p-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Header</label>
            <div
              className="text-sm text-gray-900"
              dangerouslySetInnerHTML={{
                __html: formatText(substituteParameters(header.text, content_variables))
              }}
            />
          </div>
        )}

        {/* Body */}
        {body && body.text && (
          <div className="bg-white border border-gray-200 rounded p-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Body</label>
            <div
              className="text-sm text-gray-900"
              dangerouslySetInnerHTML={{
                __html: formatText(substituteParameters(body.text, content_variables))
              }}
            />
          </div>
        )}

        {/* Footer */}
        {footer && footer.text && (
          <div className="bg-white border border-gray-200 rounded p-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Footer</label>
            <div
              className="text-sm text-gray-900"
              dangerouslySetInnerHTML={{
                __html: formatText(substituteParameters(footer.text, content_variables))
              }}
            />
          </div>
        )}

        {/* Buttons */}
        {body && body.buttons && body.buttons.length > 0 && (
          <div className="bg-white border border-gray-200 rounded p-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Buttons</label>
            <div className="space-y-2">
              {body.buttons.map((button, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {button.type}
                  </span>
                  <span className="text-sm text-gray-900">{button.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content Variables (for debugging) */}
        {content_variables && Object.keys(content_variables).length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Template Variables</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(content_variables).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-xs font-mono text-gray-600">{key}:</span>
                  <span className="text-xs text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
